import React from 'react';

import { CycleCountApi } from 'api/cycleCount';
import { AdjustCasesAndLooseUnitsAction } from 'components/warehouse/components/locationInventoryV2Components/actions/adjustCasesAndLooseUnitsAction';
import { AdjustFromZeroAction } from 'components/warehouse/components/locationInventoryV2Components/actions/adjustFromZeroAction';
import { AcknowledgeLocationIssueAction } from 'components/warehouse/components/locationInventoryV2Components/actions/cycleCountAcknowledgeIssueAction';
import { CycleCountAdjustByMagnitudeAction } from 'components/warehouse/components/locationInventoryV2Components/actions/cycleCountAdjustByMagnitudeAction';
import { MoveCasesAndLooseUnitsFromKnownOriginAction } from 'components/warehouse/components/locationInventoryV2Components/actions/moveCasesAndLooseUnitsAction';
import { MoveCasesAndLooseUnitsToKnownDestinationAction } from 'components/warehouse/components/locationInventoryV2Components/actions/moveCasesAndLooseUnitsToKnownDestinationAction';
import { MovePalletAction } from 'components/warehouse/components/locationInventoryV2Components/actions/movePalletAction';

const cycleCountApi = new CycleCountApi();

const getMovePalletActionDefinition = (currentIssue, handleCancel, handleSuccess) => ({
    text: `Move Pallet ${currentIssue?.expectedResults[0]?.expectedPalletID} from MISSING`,
    value: 'movePallet',
    componentJSX() {
        return (
            <MovePalletAction palletId={currentIssue?.expectedResults[0]?.expectedPalletID} onSuccess={handleSuccess} />
        );
    },
});

const getMoveLotFromActionDefinition = (
    currentIssue,
    handleCancel,
    handleSuccess,
    fromLocationId,
    fromLocationName,
) => {
    const expectedPalletIds = currentIssue?.expectedResults
        ?.map((item) => item?.expectedPalletID)
        ?.filter((item) => item !== null)
        ?.filter((value, index, self) => self.indexOf(value) === index);
    const expectedPalletId = expectedPalletIds.length === 1 ? expectedPalletIds[0] : null;

    return {
        text: `Move Lot ${currentIssue?.expectedResults[0]?.expectedLotID} FROM ${fromLocationName}`,
        value: 'moveFromLocation',
        componentJSX() {
            return (
                <MoveCasesAndLooseUnitsFromKnownOriginAction
                    lotId={currentIssue?.expectedResults[0]?.expectedLotID}
                    locationId={fromLocationId}
                    locationName={fromLocationName}
                    fromPalletId={expectedPalletId}
                    locInvType={expectedPalletIds.length > 0 ? 'Pallet' : 'Case'}
                    onSuccess={handleSuccess}
                    onCancel={handleCancel}
                    unitsPerCase={currentIssue?.expectedResults[0]?.unitsPerCase}
                    cases={currentIssue?.expectedCaseCount}
                    units={currentIssue?.expectedLooseUnitCount}
                />
            );
        },
    };
};

const getAdjustFromZeroActionDefinition = (currentIssue, handleCancel, handleSuccess) => ({
    text: 'Adjust From Zero',
    value: 'adjustFromZero',
    componentJSX() {
        return (
            <AdjustFromZeroAction
                lotId={currentIssue?.expectedResults[0]?.expectedLotID}
                locationId={currentIssue?.locationID}
                locationName={currentIssue?.locationName}
                palletId={currentIssue?.expectedResults[0]?.expectedPalletID}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
                reason="Cycle Count Adjustment"
                initialNumCases={currentIssue?.expectedResults[0]?.actualCaseCount}
                initialNumUnits={currentIssue?.expectedResults[0]?.actualLooseUnitCount}
            />
        );
    },
});

const getAdjustActionDefinition = (currentIssue, handleCancel, handleSuccess) => {
    const expectedPalletIds = (currentIssue?.expectedResults || [])
        .map((item) => item?.expectedPalletID)
        .filter((item) => item !== null);

    return {
        text: 'Adjust',
        value: 'adjust',
        componentJSX() {
            return (
                <CycleCountAdjustByMagnitudeAction
                    cycleCountEventId={currentIssue?.eventID}
                    expectedUnits={currentIssue?.expectedLooseUnitCount}
                    expectedCases={currentIssue?.expectedCaseCount}
                    actualUnits={currentIssue?.actualLooseUnitCount}
                    actualCases={currentIssue?.actualCaseCount}
                    lotId={currentIssue?.expectedResults[0]?.expectedLotID}
                    unitAdjustment={currentIssue?.totalUnitDifference}
                    initialReason="Cycle Count Adjustment"
                    onSuccess={handleSuccess}
                    onCancel={handleCancel}
                    locationId={currentIssue?.locationID}
                    locationName={currentIssue?.locationName}
                    palletIds={expectedPalletIds}
                />
            );
        },
    };
};

const getMoveLotToActionDefinition = (currentIssue, handleCancel, handleSuccess) => {
    const expectedPalletIds = currentIssue?.expectedResults
        ?.map((item) => item?.expectedPalletID)
        ?.filter((item) => item !== null)
        ?.filter((value, index, self) => self.indexOf(value) === index);
    const expectedPalletId = expectedPalletIds.length === 1 ? expectedPalletIds[0] : null;

    return {
        text: `Move Lot ${currentIssue?.expectedResults[0]?.expectedLotID} TO ${currentIssue.locationName}`,
        value: 'moveToLocation',
        componentJSX() {
            return (
                <MoveCasesAndLooseUnitsToKnownDestinationAction
                    lotId={currentIssue?.expectedResults[0]?.expectedLotID}
                    toLocationName={currentIssue?.locationName}
                    toLocationId={currentIssue?.locationID}
                    toPalletId={expectedPalletId}
                    onSuccess={handleSuccess}
                />
            );
        },
    };
};

const getActionDefinitions = (currentIssue, handleCancel, handleSuccess, missingLocationId, handleError) => {
    if (!currentIssue) {
        return [];
    }

    const actionDefinitions = [
        {
            text: 'Acknowledge',
            value: 'acknowledge',
            componentJSX() {
                return (
                    <AcknowledgeLocationIssueAction
                        cycleCountEventId={currentIssue.eventID}
                        locationName={currentIssue.locationName}
                        issueType={currentIssue.issueType}
                        onSuccess={handleSuccess}
                        onCancel={handleCancel}
                    />
                );
            },
        },
    ];

    const handleMovementSuccess = async (successMessage, resultData) => {
        const { movementSummaries } = resultData;
        const { summaryEventID } = movementSummaries[0];

        const res = await cycleCountApi.resolveIssue(currentIssue.eventID, summaryEventID);
        if (!res.status) {
            const message = `Movement was successful, but failed to resolve issue. Please resolve by acknowledging the issue.`;
            handleError(message);
            return;
        }

        handleSuccess(successMessage);
    };

    if (currentIssue.issueType === 'Unit Discrepancy') {
        actionDefinitions.push(getMoveLotToActionDefinition(currentIssue, handleCancel, handleMovementSuccess));
        actionDefinitions.push(
            getMoveLotFromActionDefinition(
                currentIssue,
                handleCancel,
                handleMovementSuccess,
                currentIssue.locationID,
                currentIssue.locationName,
            ),
        );
        actionDefinitions.push(getAdjustActionDefinition(currentIssue, handleCancel, handleSuccess));
    } else if (currentIssue.issueType === 'Additional Sku') {
        actionDefinitions.push(getMoveLotToActionDefinition(currentIssue, handleCancel, handleSuccess));
        actionDefinitions.push(getAdjustFromZeroActionDefinition(currentIssue, handleCancel, handleSuccess));
    } else if (currentIssue.issueType === 'Missing Pallet' || currentIssue.issueType === 'Pallet Empty') {
        actionDefinitions.push(getMovePalletActionDefinition(currentIssue, handleCancel, handleSuccess));
    } else if (currentIssue.issueType === 'Missing Lot') {
        actionDefinitions.push(
            getMoveLotFromActionDefinition(
                currentIssue,
                handleCancel,
                handleMovementSuccess,
                missingLocationId,
                'MISSING',
            ),
        );
    }

    return actionDefinitions;
};

export { getActionDefinitions };
